import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';
import * as fromRoot from '../../../../../../../../../../state/app.state';
import { dailyReport, projectDetails } from '../../../../../../../../core/projects.selectors';
import { appDataDetails } from '../../../../../../../../../../shared/core/shared.selectors';
import { ProjectsService } from '../../../../../../../../core/projects.service';
import { DailyReportService } from '../../../../daily-report.service';
import { CommonService } from '../../../../../../../../../../shared/services/common.service';
import { takeUntil } from 'rxjs/operators';
import { AddContractorDialogComponent } from '../add-contractor-dialog/add-contractor-dialog.component';

@Component({
  standalone: false,
  selector: 'app-cle-add-labor-form',
  templateUrl: './cle-add-labor-form.component.html',
  styleUrls: ['./cle-add-labor-form.component.scss'],
})
export class CleAddLaborFormComponent implements OnInit, OnDestroy {
  @Input() data: any;

  subContractorOptions = [];
  personnelTypeOptions = [
    { value: 'employee', label: 'Employee' },
    { value: 'generic_personal', label: 'Generic Personal' },
  ];
  employeeOptions = [];
  genericPersonalOptions = [];
  selectedEmployees: any[] = [];
  selectedGenericPersonals: any[] = [];
  expandedEmployees: { [key: string]: boolean } = {};
  expandedGenericPersonals: { [key: string]: boolean } = {};
  employeeForms: { [key: string]: FormGroup } = {};
  genericPersonalForms: { [key: string]: FormGroup } = {};
  form = new FormGroup<any>({
    sub_contractor: new FormControl(null, [Validators.required]),
    personnel_type: new FormControl(null, [Validators.required]),
    employees: new FormControl([]),
    generic_personals: new FormControl([]),
  });
  laborName: string = '';
  errorMessages: any = {};
  errorMessageMap: any = {
    sub_contractor: { required: 'Contractor is required' },
    personnel_type: { required: 'Personnel type is required' },
    employees: { required: 'At least one employee is required' },
    generic_personals: { required: 'At least one generic personal is required' },
  };
  formSubscription: Subscription = new Subscription();
  private readonly onDestroy: Subject<any> = new Subject<any>();

  private allContractors = [];
  private reportContractors = [];
  private pendingContractorId: string | null = null;

  constructor(
    private store: Store<fromRoot.State>,
    private projectsService: ProjectsService,
    private dailyReportService: DailyReportService,
    private commonService: CommonService,
    private dialog: MatDialog,
  ) {
  }

  ngOnInit() {
    this.laborName = this.data?.type || '';

    if (!this.data) {
      this.dailyReportService.disableOptionMenu.next(true);
    }

    this.store.select(dailyReport)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((report: any) => {
        this.reportContractors = report?.reportDetails?.sub_contractors || [];
        this.buildContractorOptions();
      });

    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(project => {
        this.allContractors = project?.sub_contractors || [];
        this.buildContractorOptions();
        this.updateEmployeeOptions();
      });

    this.store.select(appDataDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data: any) => {
        this.genericPersonalOptions = data?.genericPersonnel || [];
      });

    this.form.controls['sub_contractor'].valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.errorMessages = {};
        this.updateEmployeeOptions();
        this.form.controls['personnel_type'].setValue(null, { emitEvent: false });
        this.form.controls['employees'].setValue([]);
        this.form.controls['generic_personals'].setValue([]);
        this.selectedEmployees = [];
        this.selectedGenericPersonals = [];
        this.expandedEmployees = {};
        this.expandedGenericPersonals = {};
        this.employeeForms = {};
        this.genericPersonalForms = {};
      });

    this.form.controls['personnel_type'].valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(value => {
        this.errorMessages = {};
        this.form.controls['employees'].setValue([], { emitEvent: false });
        this.form.controls['generic_personals'].setValue([], { emitEvent: false });
        this.form.controls['employees'].setErrors(null);
        this.form.controls['generic_personals'].setErrors(null);
        this.selectedEmployees = [];
        this.selectedGenericPersonals = [];
        this.expandedEmployees = {};
        this.expandedGenericPersonals = {};
        this.employeeForms = {};
        this.genericPersonalForms = {};

        if (value === 'employee') {
          this.form.controls['employees'].setValidators([Validators.required]);
          this.form.controls['generic_personals'].clearValidators();
        } else if (value === 'generic_personal') {
          this.form.controls['generic_personals'].setValidators([Validators.required]);
          this.form.controls['employees'].clearValidators();
        } else {
          this.form.controls['employees'].clearValidators();
          this.form.controls['generic_personals'].clearValidators();
        }
        this.form.controls['employees'].updateValueAndValidity({ emitEvent: false });
        this.form.controls['generic_personals'].updateValueAndValidity({ emitEvent: false });
      });

    this.form.controls['employees'].valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(selectedIds => {
        this.onEmployeeSelectionChange(selectedIds || []);
      });

    this.form.controls['generic_personals'].valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(selectedIds => {
        this.onGenericPersonalSelectionChange(selectedIds || []);
      });

    const initialValues = JSON.stringify(this.form.getRawValue());
    this.formSubscription = this.form.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        const currentValues = JSON.stringify(this.form.getRawValue());
        if (currentValues !== initialValues) {
          this.projectsService.isAnyFormDirty.next(true);
          this.formSubscription?.unsubscribe();
        }
      });

    this.dailyReportService.triggerSave
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        if (this.validate()) {
          this.save();
        }
      });

    this.dailyReportService.triggerDelete
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.delete();
      });
  }

  checkForErrors(currentField?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField)),
    };
  }

  validate(): boolean {
    if (this.form.invalid) {
      this.checkForErrors();
      return false;
    }
    return true;
  }

  private buildContractorOptions() {
    this.subContractorOptions = this.reportContractors.map(sc => {
      const projectContractor = this.allContractors.find(c => c.value === sc.sub_contractor?.id);
      return {
        value: sc.sub_contractor?.id,
        label: sc.sub_contractor?.name,
        id: sc.sub_contractor?.id,
        tag: projectContractor?.tag || null,
      };
    }).sort((a, b) => {
      const aPrime = a.tag ? 1 : 0;
      const bPrime = b.tag ? 1 : 0;
      return bPrime - aPrime;
    });

    if (this.pendingContractorId) {
      const found = this.subContractorOptions.find(o => o.value === this.pendingContractorId);
      if (found) {
        this.form.controls['sub_contractor'].setValue(this.pendingContractorId);
        this.pendingContractorId = null;
      }
    }
  }

  private updateEmployeeOptions() {
    const contractorId = this.form.controls['sub_contractor'].value;
    if (contractorId) {
      const contractor = this.allContractors.find(c => c.value === contractorId);
      this.employeeOptions = contractor?.employees || [];
    } else {
      this.employeeOptions = [];
    }
  }

  onEmployeeSelectionChange(selectedIds: any[]) {
    this.selectedEmployees = this.employeeOptions.filter(o => selectedIds.includes(o.value));
    selectedIds.forEach(id => {
      if (!this.employeeForms[id]) {
        this.employeeForms[id] = new FormGroup({
          hours: new FormControl(null),
          comment: new FormControl(null),
        });
      }
    });
    Object.keys(this.employeeForms).forEach(key => {
      if (!selectedIds.includes(key)) {
        delete this.employeeForms[key];
      }
    });
  }

  onGenericPersonalSelectionChange(selectedIds: any[]) {
    this.selectedGenericPersonals = this.genericPersonalOptions.filter(o => selectedIds.includes(o.value));
    selectedIds.forEach(id => {
      if (!this.genericPersonalForms[id]) {
        this.genericPersonalForms[id] = new FormGroup({
          quantity: new FormControl(null),
          hours: new FormControl(null),
          comment: new FormControl(null),
        });
      }
    });
    Object.keys(this.genericPersonalForms).forEach(key => {
      if (!selectedIds.includes(key)) {
        delete this.genericPersonalForms[key];
      }
    });
  }

  toggleAccordion(employee: any) {
    const isExpanded = this.expandedEmployees[employee.value];
    this.expandedEmployees = {};
    if (!isExpanded) {
      this.expandedEmployees[employee.value] = true;
    }
  }

  toggleGenericPersonalAccordion(gp: any) {
    const isExpanded = this.expandedGenericPersonals[gp.value];
    this.expandedGenericPersonals = {};
    if (!isExpanded) {
      this.expandedGenericPersonals[gp.value] = true;
    }
  }

  openAddContractorDialog() {
    const dialogRef = this.dialog.open(AddContractorDialogComponent, {
      disableClose: true,
      width: '450px',
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result?.sub_contractor_id) {
        const existing = this.subContractorOptions.find(o => o.value === result.sub_contractor_id);
        if (existing) {
          this.form.controls['sub_contractor'].setValue(result.sub_contractor_id);
        } else {
          this.pendingContractorId = result.sub_contractor_id;
        }
      }
    });
  }

  save() {
    const subContractorId = this.form.controls['sub_contractor'].value;
    const personnelType = this.form.controls['personnel_type'].value;
    const laborEntries = [];

    if (personnelType === 'employee') {
      const selectedIds = this.form.controls['employees'].value || [];
      selectedIds.forEach(id => {
        const empForm = this.employeeForms[id]?.getRawValue();
        laborEntries.push({
          id: `new_${new Date().getTime()}_${id}`,
          source_type: 'employees',
          source_id: id,
          sub_contractor_id: subContractorId,
          quantity: 1,
          duration: empForm?.hours || 0,
          idle_duration: 0,
          comment: empForm?.comment || null,
          pictures: [],
          videos: [],
          field_values: [],
        });
      });
    } else if (personnelType === 'generic_personal') {
      const selectedIds = this.form.controls['generic_personals'].value || [];
      selectedIds.forEach(id => {
        const gpForm = this.genericPersonalForms[id]?.getRawValue();
        laborEntries.push({
          id: `new_${new Date().getTime()}_${id}`,
          source_type: 'genericPersonnel',
          source_id: id,
          sub_contractor_id: subContractorId,
          quantity: gpForm?.quantity || 0,
          duration: gpForm?.hours || 0,
          idle_duration: 0,
          comment: gpForm?.comment || null,
          pictures: [],
          videos: [],
          field_values: [],
        });
      });
    }

    this.dailyReportService.save.next({
      type: 'labor',
      data: laborEntries,
    });
  }

  delete() {
    this.dailyReportService.delete.next({
      type: 'labor',
      id: this.data?.id,
    });
  }

  ngOnDestroy() {
    this.formSubscription?.unsubscribe();
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
